/**
 * src/ui/como-se-juega-modal.js — modal de onboarding "Cómo se juega".
 *
 * Feedback de la prueba con amigos: al entrar a un grupo hay mucha info y nadie
 * entiende rápido cómo se juega. Este modal resume las reglas en versión
 * ultra-compacta (~1 min de lectura). Se abre desde el botón "🏆 ¿Cómo se
 * juega?" del home del grupo (entre la card del Pacto y "Próximos partidos") y
 * se auto-abre la PRIMERA vez que el usuario entra a un grupo en este device.
 *
 * Flag: localStorage 'pm_csj_visto'. Se setea AL CERRAR (no al abrir): si el
 * usuario recarga sin cerrar, lo vuelve a ver. Las 3 vías de cierre (X, CTA al
 * pie, tap en el backdrop) cierran y setean el flag.
 *
 * Mismo patrón que src/ui/jugadores-modal.js: HTML puro exportado para test +
 * CSS inyectado una sola vez (idempotente) + open/close + bridge a window para
 * el onclick="" inline del botón en grupo.js. Reutiliza el lenguaje visual
 * existente (glass, --white/--text-muted/--celeste, --font-heading, radius 14).
 */

export const STORAGE_KEY = 'pm_csj_visto';
const MODAL_ID = 'comoSeJuegaModal';
const STYLE_ID = 'como-se-juega-modal-css';

// Copy canónico — NO reescribir. Cada sección: emoji+título + 2-4 líneas.
// Los tramos entre **dobles asteriscos** se renderizan en <strong> (ver applyBold).
const SECCIONES = [
    {
        titulo: '🤝 PACTO',
        lineas: [
            'Elijan **PREMIO** para el campeón, **CASTIGO** para el último',
            'Firmen, certifiquen y a jugar 🎉',
        ],
    },
    {
        titulo: '⚽ PRODE',
        lineas: [
            'Cada partido muestra sus puntos en juego',
            '**Fase de grupos:** gana - empata - pierde',
            '**Playoffs:** quién pasa, goles y diferencia',
            '**🔥 En playoffs los puntos se multiplican**',
        ],
    },
    {
        titulo: '📣 RITUALES',
        lineas: [
            'Arengas · Cábalas · Promesas',
            '+1 por crear',
            '+1 por cada reacción recibida',
            '**🚀 Un ritual viral puede cambiarlo todo**',
        ],
    },
    {
        titulo: '🌎 TRIBUNA PÚBLICA',
        lineas: [
            'Podés hacer públicos tus rituales',
            'Miles pueden verlos y reaccionar',
            '🔥 Más reacciones = más puntos',
        ],
    },
    {
        titulo: '📈 RANKING',
        lineas: [
            'PRODE + RITUALES = PUNTAJE TOTAL',
            '🥇 Más puntos: **campeón** · 🥄 Menos puntos: **castigo**',
        ],
    },
];

// Convierte los tramos **así** en <strong>así</strong>. Non-greedy, soporta
// varios por línea (ej. "**premio** ... **castigo**") y líneas enteras en bold.
const applyBold = (linea) => linea.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

// Botón "trigger" que vive en el home del grupo. Exportado para que grupo.js lo
// interpole y el test lo verifique sin renderizar todo el home.
export const buildComoSeJuegaButton = () => `
    <button class="btn btn-secondary csj-trigger-btn" type="button" onclick="window.openComoSeJuegaModal()" style="width:100%; margin-bottom:1rem;">🏆 ¿Cómo se juega?</button>
`;

// HTML puro del modal — exportado para test.
export const buildComoSeJuegaModalHTML = () => {
    const secciones = SECCIONES.map((s) => `
        <div class="csj-section">
            <h3 class="csj-section-title">${s.titulo}</h3>
            ${s.lineas.map((l) => `<p class="csj-section-line">${applyBold(l)}</p>`).join('')}
        </div>
    `).join('');

    return `
        <div id="${MODAL_ID}" class="csj-overlay" role="dialog" aria-modal="true" aria-label="Cómo se juega">
            <div class="csj-card glass-card">
                <header class="csj-header">
                    <h2 class="csj-title">🏆 CÓMO SE JUEGA</h2>
                    <button class="csj-close" type="button" aria-label="Cerrar">✕</button>
                </header>
                <div class="csj-body">
                    ${secciones}
                </div>
                <footer class="csj-footer">
                    <button class="btn btn-primary csj-cta" type="button">¡A jugar!</button>
                </footer>
            </div>
        </div>
    `;
};

// CSS inyectado una sola vez (idempotente).
const MODAL_CSS = `
    .csj-overlay {
        position: fixed; inset: 0; z-index: 100;
        background: rgba(0,0,0,.68); backdrop-filter: blur(6px);
        display: flex; align-items: center; justify-content: center;
        padding: 1rem; animation: csjModalIn .18s ease-out;
    }
    @keyframes csjModalIn { from { opacity: 0; } to { opacity: 1; } }
    .csj-card {
        position: relative; width: min(100%, 420px); max-height: calc(100dvh - 2rem);
        display: flex; flex-direction: column; padding: 0; overflow: hidden;
        border-radius: 16px;
        background: radial-gradient(circle at 14% 0%, rgba(117,170,219,.12), transparent 40%), rgba(12,18,30,.98);
        border: 1px solid rgba(117,170,219,.22);
    }
    .csj-header {
        flex: 0 0 auto; display: flex; align-items: center; justify-content: space-between;
        gap: .6rem; padding: 1rem 1.1rem .8rem;
        border-bottom: 1px solid rgba(255,255,255,.07);
        background: rgba(12,18,30,.6);
    }
    .csj-title {
        margin: 0; color: var(--white);
        font-family: var(--font-heading); font-size: 1.02rem;
        letter-spacing: .04em; line-height: 1.1; text-transform: uppercase;
    }
    .csj-close {
        flex: 0 0 auto; width: 30px; height: 30px; border-radius: 999px;
        border: 1px solid rgba(255,255,255,.14); background: rgba(255,255,255,.05);
        color: var(--white); font-size: .85rem; cursor: pointer;
        display: inline-flex; align-items: center; justify-content: center;
    }
    .csj-close:hover { background: rgba(255,255,255,.1); }
    .csj-body {
        flex: 1 1 auto; overflow-y: auto; padding: .9rem 1.1rem;
        display: flex; flex-direction: column; gap: .6rem;
    }
    .csj-section {
        padding: .7rem .8rem; border-radius: 11px;
        background: rgba(255,255,255,.035); border: 1px solid rgba(255,255,255,.07);
    }
    .csj-section-title {
        margin: 0 0 .35rem; color: var(--white);
        font-family: var(--font-heading); font-size: .92rem;
        font-weight: 900; letter-spacing: .03em;
    }
    .csj-section-line {
        margin: 0 0 .16rem; color: var(--text-muted);
        font-size: .82rem; line-height: 1.32;
    }
    .csj-section-line:last-child { margin-bottom: 0; }
    .csj-footer {
        flex: 0 0 auto; padding: .8rem 1.1rem 1rem;
        border-top: 1px solid rgba(255,255,255,.07);
        background: rgba(12,18,30,.6);
    }
    .csj-cta { width: 100%; margin: 0; }
`;

const injectCSS = () => {
    if (typeof document === 'undefined' || !document.head) return;
    try {
        if (!document.getElementById?.(STYLE_ID)) {
            const styleEl = document.createElement('style');
            styleEl.id = STYLE_ID;
            styleEl.textContent = MODAL_CSS;
            document.head.appendChild?.(styleEl);
        }
    } catch { /* polyfills mínimos de test no tienen head completo — no romper imports */ }
};
injectCSS();

// ─── Flag helpers (localStorage, guardados para node/tests) ─────────────────

const marcarVisto = () => {
    try { if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, '1'); }
    catch { /* localStorage bloqueado (modo privado) — no romper */ }
};

export const yaVioComoSeJuega = () => {
    try { return typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY) === '1'; }
    catch { return false; }
};

// ─── API pública ────────────────────────────────────────────────────────────

const escListener = (e) => { if (e.key === 'Escape') closeComoSeJuegaModal(); };

export const openComoSeJuegaModal = () => {
    if (typeof document === 'undefined') return;
    closeComoSeJuegaModal();           // idempotente: nunca dos modales
    injectCSS();                       // defensivo si el import corrió sin head
    const container = document.createElement('div');
    container.innerHTML = buildComoSeJuegaModalHTML();
    const modal = container.firstElementChild;
    if (!modal) return;
    document.body.appendChild(modal);
    // Las 3 vías de cierre setean el flag (vía closeComoSeJuegaModal).
    modal.querySelector('.csj-close')?.addEventListener('click', closeComoSeJuegaModal);
    modal.querySelector('.csj-cta')?.addEventListener('click', closeComoSeJuegaModal);
    modal.addEventListener('click', (e) => { if (e.target === modal) closeComoSeJuegaModal(); });
    document.addEventListener('keydown', escListener);
};

export const closeComoSeJuegaModal = () => {
    if (typeof document === 'undefined') return;
    const existente = document.getElementById(MODAL_ID);
    // Solo marcamos visto cuando había un modal real que cerrar (evita setear
    // el flag por el close() preventivo de openComoSeJuegaModal).
    if (existente) {
        existente.remove();
        marcarVisto();
    }
    document.removeEventListener?.('keydown', escListener);
};

// Auto-apertura la primera vez (sin flag). Devuelve true si abrió.
export const maybeAutoOpenComoSeJuega = () => {
    if (yaVioComoSeJuega()) return false;
    openComoSeJuegaModal();
    return true;
};

// Bridges para los onclick="" inline y para grupo.js.
if (typeof window !== 'undefined') {
    window.openComoSeJuegaModal = openComoSeJuegaModal;
    window.closeComoSeJuegaModal = closeComoSeJuegaModal;
}
